import React, { useEffect, useRef } from 'react';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { cn } from '../../lib/utils';

export function FlyoutRailBackdrop({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="presentation"
      className="fixed inset-0 cursor-default bg-black/40 dark:bg-black/55 backdrop-blur-[1px]"
      style={zStyle(Z_INDEX.shellFlyoutBackdrop)}
      aria-hidden
      onClick={onClose}
    />
  );
}

export interface FlyoutRailPanelProps {
  left: number;
  ariaLabel: string;
  children: React.ReactNode;
  className?: string;
  /** Extra inline styles (e.g. dynamic width) merged after positioning */
  panelStyle?: React.CSSProperties;
  /** `floating` = inset card beside the rail; `full` = edge-to-edge panel (legacy) */
  variant?: 'full' | 'floating';
  /** Gap between rail and floating panel (px) */
  railGap?: number;
}

/**
 * Full-height panel anchored to the right edge of a collapsed rail; matches app/email sidebar flyouts.
 * Focuses first focusable on mount, basic Tab wrap inside panel, restores focus to previously focused element on unmount.
 */
export function FlyoutRailPanel({
  left,
  ariaLabel,
  children,
  className,
  panelStyle,
  variant = 'full',
  railGap = 8,
}: FlyoutRailPanelProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    return () => {
      if (prev && typeof prev.focus === 'function' && document.body.contains(prev)) {
        prev.focus();
      }
    };
  }, []);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const t = window.setTimeout(() => {
      const focusable = root.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const nodes = Array.from(
        root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    root.addEventListener('keydown', handler);
    return () => root.removeEventListener('keydown', handler);
  }, []);

  const isFloating = variant === 'floating';

  return (
    <div
      ref={ref}
      className={cn(
        'fixed flex flex-col',
        isFloating
          ? 'top-3 bottom-3 border shadow-2xl backdrop-blur-xl'
          : 'top-0 bottom-0 border-r shadow-2xl backdrop-blur-sm',
        className
      )}
      style={{
        left: isFloating ? left + railGap : left,
        ...zStyle(Z_INDEX.shellFlyoutPanel),
        ...panelStyle,
      }}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
}
